/**
 * F-LIVE2-41 — what the RUN's variable package actually is, read off the head.
 *
 * The HB-5 override panel used to state `Object.keys(strategyDoc.variables)` —
 * the head's whole variable collection («29 strategy variables») — while the run
 * sent 23 (`variablePackageManifest.finalVariableCount: 23`, the resolver's own
 * `variablePackageCounts.final: 23`). The read-only fact must state what the run
 * sends, so this helper MIRRORS the server's two rules, client-side and pure:
 *
 *   `BlockScreenGenerationContextResolver` (HB-4) decides the SOURCE —
 *     `head-package`   the head carries a `variablePackageManifest`
 *     `head-variables` it carries only a variables record (legacy)
 *
 *   `resolveStrategyVariablePackage` decides the CONTENT — the merged source
 *     ids (project → insight → explicit → task-router, in that order) filtered
 *     by `selectedVariableIds` UNIONED with the ids the insight plane
 *     contributed (the insight plane is narrowed by its own option first, so it
 *     is exempt from the project plane's filter); an empty `selectedVariableIds`
 *     means «no narrowing» and keeps every merged id.
 *
 * Nothing here imports server code — it reads only the manifest the server
 * already froze onto the head.
 *
 * And it POLICES ITSELF (fix round 1, review Minor #7): when the manifest
 * carries `finalVariableCount` — the one authoritative post-filter number the
 * server persists — the reconstruction is checked against it. A mismatch means
 * the mirror has drifted from the resolver, which is F-LIVE2-41 recurring, so
 * the helper says `'unreconstructed'` instead of publishing a confident split
 * that does not add up.
 */

/** The insight source's name in `variablePackageManifest.sources`. */
const INSIGHT_SOURCE = 'insight_variable_generation';
/**
 * Planes `selectedVariableIds` never narrows. The insight plane is narrowed by
 * its OWN option before the merge (`selectedInsightVariableIds`), and the
 * task-router declaration is APPENDED by `applyTaskRouterDeclarationToPackage`
 * after the filter has already run.
 */
const UNFILTERED_SOURCES = new Set([INSIGHT_SOURCE, 'task-router']);

export interface HeadVariablePackage {
  /** The ids the run's package carries, in the server's own merge order. */
  ids: string[];
  /**
   * Which rule produced them — the resolver's own `variablePackageSource` words,
   * plus `'unreconstructed'`: a frozen manifest exists but the client cannot
   * reproduce it — it lists no `sources`, or the reconstruction disagreed with
   * its `finalVariableCount` — so `ids` is the head's own record and no split is
   * offered.
   */
  source: 'head-package' | 'head-variables' | 'none' | 'unreconstructed';
  /**
   * The split of `ids`, derived from the ids themselves so the sentence and the
   * chips can never count different things. Absent without a manifest.
   */
  insightVariableCount?: number;
  projectVariableCount?: number;
}

const stringIds = (value: unknown): string[] => (Array.isArray(value)
  ? value.filter((id): id is string => typeof id === 'string' && id.length > 0)
  : []);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

/**
 * Resolves the package a generation from this head SENDS.
 *
 * @param head the strategy head document (`variablePackageManifest`, `variables`)
 */
export function resolveHeadVariablePackage(head: unknown): HeadVariablePackage {
  const doc = isRecord(head) ? head : {};
  const manifest = isRecord(doc.variablePackageManifest) ? doc.variablePackageManifest : undefined;
  const headVariableIds = isRecord(doc.variables) ? Object.keys(doc.variables) : [];

  // `serverContextHasValues` on the resolver's side: an EMPTY manifest is no
  // manifest, and the head's own record answers instead.
  if (!manifest || Object.keys(manifest).length === 0) {
    return headVariableIds.length > 0
      ? { ids: headVariableIds, source: 'head-variables' }
      : { ids: [], source: 'none' };
  }

  const sources = Array.isArray(manifest.sources) ? manifest.sources : [];
  const merged: string[] = [];
  const seen = new Set<string>();
  const insightIds = new Set<string>();
  const unfilteredIds = new Set<string>();
  for (const entry of sources) {
    if (!isRecord(entry)) continue;
    const ids = stringIds(entry.variableIds);
    if (entry.source === INSIGHT_SOURCE) ids.forEach((id) => insightIds.add(id));
    if (typeof entry.source === 'string' && UNFILTERED_SOURCES.has(entry.source)) {
      ids.forEach((id) => unfilteredIds.add(id));
    }
    for (const id of ids) {
      if (seen.has(id)) continue;
      seen.add(id);
      merged.push(id);
    }
  }

  /**
   * A manifest with no `sources` cannot be reconstructed — the head's own record
   * is then the closest honest answer.
   *
   * Final review Minor #7: the WORD for it is `'unreconstructed'`, not
   * `'head-variables'`. A manifest EXISTS, so the server labels this same head
   * `head-package`, and the panel's `head-variables` caption («The strategy
   * carries no frozen variable package») contradicted it. `'unreconstructed'` is
   * exactly what this is — a frozen package the client cannot reproduce — and its
   * caption says so.
   */
  if (merged.length === 0) {
    return { ids: headVariableIds, source: 'unreconstructed' };
  }

  const selected = stringIds(manifest.selectedVariableIds);
  // Two planes, one filter — the resolver's rule verbatim: the insight plane's
  // survivors join the selection instead of being re-filtered against it.
  const finalSelected = selected.length > 0
    ? new Set<string>([...selected, ...unfilteredIds])
    : undefined;
  const ids = finalSelected ? merged.filter((id) => finalSelected.has(id)) : merged;

  /**
   * The split is derived from `ids`, so it ALWAYS sums to them — the panel
   * refuses a split that disagrees with its chips, and the live sighting was
   * exactly that disagreement. Everything that is not insight-derived (project,
   * explicit, the task-router declaration) is the strategy's own half.
   */
  const insightVariableCount = ids.filter((id) => insightIds.has(id)).length;

  /**
   * The cross-check. `finalVariableCount` is what the server counted AFTER its
   * own filter, so an honest mirror reproduces it exactly; a legacy manifest
   * that does not carry the number is simply not checked.
   */
  const finalCount = manifest.finalVariableCount;
  if (typeof finalCount === 'number' && Number.isFinite(finalCount) && finalCount !== ids.length) {
    return { ids: headVariableIds, source: 'unreconstructed' };
  }

  return {
    ids,
    source: 'head-package',
    insightVariableCount,
    projectVariableCount: ids.length - insightVariableCount,
  };
}
